import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const BRAND_SEARCH_RESOURCE = "brand" as const;
export const BRAND_SEARCH_OPERATION = "search" as const;
export const BRAND_SEARCH_API_PATH = `/v1/${BRAND_SEARCH_RESOURCE}/${BRAND_SEARCH_OPERATION}` as const;

export type BrandSearchParameters = paths[typeof BRAND_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type BrandSearchBody = paths[typeof BRAND_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type BrandSearchResponse =
  paths[typeof BRAND_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useBrandSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<BrandSearchParameters, BrandSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<BrandSearchResponse, Error, BrandSearchResponse, readonly unknown[]>;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<BrandSearchParameters, BrandSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      BRAND_SEARCH_RESOURCE,
      BRAND_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<BrandSearchResponse>(
        BRAND_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
